import React, { useEffect, useState } from 'react'
import { ModalLayout } from './HeadlessUI'
import { MODAL_CONFIRM_PANEL_CLASS, MODAL_PLACEMENT_CENTER } from '@/constants'
import { useTranslation } from 'react-i18next'

interface ConfirmationModalProps {
	isOpen: boolean
	onClose: () => void
	onConfirm: (reason?: string) => void | Promise<void>
	title?: string
	description?: string
	confirmButtonText?: string
	variant?: 'primary' | 'destructive' | 'warning'
	/** When true, shows a required textarea and blocks confirm until it has text. */
	requireReason?: boolean
	reasonLabel?: string
	reasonPlaceholder?: string
	initialReason?: string
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
	isOpen,
	onClose,
	onConfirm,
	title = 'Confirmation',
	description = 'Are you sure you want to proceed?',
	confirmButtonText = 'Confirm',
	variant = 'primary',
	requireReason = false,
	reasonLabel = 'Reason',
	reasonPlaceholder = 'Enter a reason…',
	initialReason = '',
}) => {
	const { t } = useTranslation()
	const isDestructive = variant === 'destructive'
	const isWarning = variant === 'warning'
	const [reason, setReason] = useState(initialReason)
	const [confirming, setConfirming] = useState(false)

	useEffect(() => {
		if (isOpen) {
			setReason(initialReason)
			setConfirming(false)
		}
	}, [isOpen, initialReason])

	const canConfirm = !confirming && (!requireReason || reason.trim().length > 0)

	const accentClass = isDestructive ? 'text-danger bg-danger/10' : isWarning ? 'text-amber-500 bg-amber-500/10' : 'text-primary bg-primary/10'

	const icon = isDestructive ? (
		<i className="ri-alert-fill text-4xl" aria-hidden />
	) : isWarning ? (
		<i className="ri-error-warning-fill text-4xl" aria-hidden />
	) : (
		<i className="ri-information-fill text-4xl" aria-hidden />
	)

	const confirmBtnClass = isDestructive ? 'btn btn-danger' : isWarning ? 'btn bg-amber-600 text-white hover:brightness-110' : 'btn btn-primary'

	return (
		<ModalLayout showModal={isOpen} toggleModal={onClose} panelClassName={MODAL_CONFIRM_PANEL_CLASS} placement={MODAL_PLACEMENT_CENTER}>
			<div className="flex flex-col overflow-hidden rounded-app border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-[hsl(var(--card-foreground))] shadow-2xl">
				<div className={`border-b border-[hsl(var(--border))] px-6 py-5 text-center ${accentClass}`}>
					{icon}
					<h4 className="mt-3 text-xl font-semibold text-[hsl(var(--foreground))]">{t(title)}</h4>
				</div>
				<div className="px-6 py-5 text-center">
					<p className="text-[hsl(var(--muted-foreground))]">{t(description)}</p>
					{requireReason && (
						<div className="mt-4 text-left">
							<label className="mb-1 block text-sm font-medium text-[hsl(var(--foreground))]">
								{t(reasonLabel)} <span className="text-danger">*</span>
							</label>
							<textarea
								className="form-textarea w-full"
								rows={3}
								value={reason}
								placeholder={t(reasonPlaceholder) ?? ''}
								onChange={(e) => setReason(e.target.value)}
							/>
						</div>
					)}
					<div className="mt-6 flex flex-wrap justify-center gap-2">
						<button type="button" className="btn btn-secondary" onClick={onClose}>
							{t('Manage.Helper.Delete_Cancel', 'Cancel')}
						</button>
						<button
							type="button"
							className={`${confirmBtnClass} ${!canConfirm ? 'cursor-not-allowed opacity-50' : ''}`}
							disabled={!canConfirm}
							onClick={async () => {
								if (!canConfirm) return
								setConfirming(true)
								try {
									await onConfirm(requireReason ? reason.trim() : undefined)
									onClose()
								} catch {
									// Error feedback is handled by the caller, usually through runWithToast.
								} finally {
									setConfirming(false)
								}
							}}
						>
							{confirming && <i className="ri-loader-4-line animate-spin" aria-hidden />}
							{confirming ? t('Manage.Helper.Saving', 'Saving...') : t(confirmButtonText)}
						</button>
					</div>
				</div>
			</div>
		</ModalLayout>
	)
}

export default ConfirmationModal
